import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useWatchAuth } from '@components/auth/hooks';
import PropTypes from 'prop-types';
import { LinearProgress } from '@mui';
import { authLogout, setAgencySuccess } from '@actions/auth-actions';
import { fetchDataTypes } from '@actions/data-types-actions';
import { pollUnreadNotificationsAsync } from '@actions/notifications-actions';
import { paper } from '@constants/mui-theme';
import { dataTypesLoaderSelector } from '@selectors/data-types-selector';
import Header from '@components/header';
import { getAgencyId } from '@utils/permission-utils';
import ActionConfirmationDialog from './confirmation-dialog';
import ErrorSnackbar from './error-snackbar';
import InfoSnackbar from './info-snackbar';
import useIdleMonitor from './idle-monitor';
import Disclaimer from './disclaimer';
import AgencySelect from './agency-select';
import WarningDialog from './warning-dialog';
import SomFooter from '@shared/som-footer';
import { getConfig } from '@components/config/selectors';
import { getConfigKey } from '@utils/config-utils';
import './authenticated.scss';

const AuthenticatedLayout = ({ children }) => {
  const dispatch = useDispatch();
  const loader = useSelector(dataTypesLoaderSelector);
  const {userLoaded} = useSelector(getConfig);

  // Handle idle logout
  const onIdle = useCallback(() => {
    dispatch(authLogout());
  }, [dispatch]);

  // Use idle monitor hook
  useIdleMonitor(onIdle);

  // Initialize on mount
  useEffect(() => {
    dispatch(fetchDataTypes());
    dispatch(setAgencySuccess(getAgencyId()));
    window.stopPolling = false;
    dispatch(pollUnreadNotificationsAsync());

    // Cleanup on unmount
    return () => {
      window.stopPolling = true;
    };
  }, [dispatch]);

  useWatchAuth();

  const { count, total } = loader;
  const loaded = count === total && userLoaded;
  const somFooterEnabled = getConfigKey('somDigitalStandards')?.footer?.enabled;
  const progress = useMemo(() => {
    if (userLoaded) {
      return 100 * (count + 1) / (total + 1);
    }
    return 100 * (count) / (total + 1);
  }, [count, total, userLoaded]);

  return (
    <div styleName="app-container">
      <a href="#main-content" styleName="skip-nav">
        Skip to main content
      </a>
      <header
        role="banner"
        styleName="header-container"
        style={{
          ...paper,
          boxShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 6px, rgba(0, 0, 0, 0.12) 0px 1px 4px'
        }}
      >
        <Header />
      </header>
      <main
        role="main"
        id="main-content"
        styleName={`content-container${somFooterEnabled ? ' content-container-with-footer' : ''}`}
      >
        {loaded
          ? children
          : <LinearProgress variant="determinate" value={progress} />}
      </main>
      <InfoSnackbar />
      <ErrorSnackbar />
      <ActionConfirmationDialog />
      <WarningDialog />
      <AgencySelect />
      <Disclaimer />
      <SomFooter />
    </div>
  );
};

AuthenticatedLayout.propTypes = {
  children: PropTypes.element
};

export default AuthenticatedLayout;
